import React, { useState, useEffect } from "react";
import { saveAs } from 'file-saver';
import "../css/StudentsSection.css"; 

function StudentsSection() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allotPrefProject, setAllotPrefProject] = useState("");

  // Fetch all students with their preferences
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/admin/students", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch students");
      }

      const data = await response.json();
      setStudents(data);
      setFilteredStudents(data);
      
      // If a student is already selected, refresh their details
      if (selectedStudent) {
        const refreshedStudent = data.find(s => s.sip_id === selectedStudent.sip_id);
        if (refreshedStudent) setSelectedStudent(refreshedStudent);
      }
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch student details by SIP ID
  const fetchStudentDetails = async (sipId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/admin/students/${sipId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch student details");
      }

      const data = await response.json();
      setSelectedStudent(data);
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get unique projects from student preferences
  const getAvailableProjects = () => {
    if (!selectedStudent) return [];
    
    const projects = [];
    const addedIds = new Set();
    
    // Add preferences if they exist and haven't been added yet
    [selectedStudent.pref1, selectedStudent.pref2, selectedStudent.pref3].forEach(pref => {
      if (pref && !addedIds.has(pref.id)) {
        projects.push(pref);
        addedIds.add(pref.id);
      }
    });
    
    return projects;
  };

  // Allot project to student
  const handleAllotProject = async () => {
    if (!selectedStudent || !selectedProject) {
      setMessage("Please select both a student and a project");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `/api/admin/student/allotment/${selectedStudent.sip_id}/${selectedProject}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to allot project");
      }

      const data = await response.json();
      setMessage(data.message || "Project allotted successfully!");
      fetchStudents(); // Refresh the list to show updated project status
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Remove project from student
  const handleRemoveProject = async () => {
    if (!selectedStudent) {
      setMessage("Please select a student");
      return;
    }

    if (!selectedStudent.selected_project) {
      setMessage("This student has no project allotted");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `/api/admin/student/allotment/${selectedStudent.sip_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to remove project");
      }

      const data = await response.json();
      setMessage(data.message || "Project removed successfully!");
      fetchStudents(); // Refresh the list to show updated project status
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Allot preference to student
  const handleAllotPref = async () => {
    if (!selectedStudent || !allotPrefProject) {
      setMessage("Please select a project");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/admin/allot_pref", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sip_id: selectedStudent.sip_id,
          project_id: allotPrefProject,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to allot preference");
      }

      const data = await response.json();
      setMessage(data.message || "Preference allotted successfully!");
      fetchStudents(); // Refresh the list to show updated project status
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Download all students as CSV
  const handleDownloadStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/admin/students/excel", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to download students");
      }

      const blob = await response.blob();
      saveAs(blob, "students_data.csv");
      setMessage("Download started!");
    } catch (error) {
      setMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
const handleSearch = (e) => {
  const term = e.target.value.toLowerCase();
  setSearchTerm(term);
  
  if (term === "") {
    setFilteredStudents(students);
  } else {
    const filtered = students.filter(student => {
      // Helper function to safely convert any value to lowercase string
      const safeToLower = (value) => String(value || '').toLowerCase();
      
      return (
        safeToLower(student.sip_id).includes(term) ||
        safeToLower(student.name).includes(term) ||
        safeToLower(student.institution).includes(term) ||
        safeToLower(student.selected_project?.title).includes(term) ||
        safeToLower(student.pref1?.title).includes(term) ||
        safeToLower(student.pref2?.title).includes(term) ||
        safeToLower(student.pref3?.title).includes(term)
      );
    });
    setFilteredStudents(filtered);
  }
};

  useEffect(() => {
    fetchStudents();
  }, []);

  const availableProjects = selectedStudent ? getAvailableProjects() : [];

  return (
    <div className="students-section">
      <h2>Students Management</h2>
      
      {message && <div className="alert">{message}</div>}
      
      <div className="search-and-download">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by SIP ID, Name, Institution or Project..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <i className="fas fa-search"></i>
        </div>
        <button 
          onClick={handleDownloadStudents} 
          disabled={loading}
          className="download-btn"
        >
          {loading ? "Downloading..." : "Download All"}
        </button>
      </div>
      
      <div className="students-container">
        <div className="students-list-container">
          <div className="section-header">
            <h3>All Students</h3>
            <span className="student-count">{filteredStudents.length} students</span>
          </div>
          
          {loading && !students.length ? (
            <div className="loading">Loading students...</div>
          ) : filteredStudents.length === 0 ? (
            <div className="no-results">No students found matching your search</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>SIP ID</th>
                    <th>Name</th>
                    {/* <th>Institution</th> */}
                    <th>Selected Project</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr 
                      key={student.sip_id} 
                      onClick={() => fetchStudentDetails(student.sip_id)}
                      className={selectedStudent?.sip_id === student.sip_id ? "selected" : ""}
                    >
                      <td>{student.sip_id}</td>
                      <td>{student.name}</td>
                      {/* <td>{student.institution}</td> */}
                      <td>
                        {student.selected_project?.title || "Not selected"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="student-details-container">
          {selectedStudent ? (
            <div className="student-details">
              <h3>Student Details - {selectedStudent.sip_id}</h3>
              
              <div className="detail-group">
                <h4>Personal Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedStudent.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedStudent.user.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{selectedStudent.phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date of Birth:</span>
                  <span className="detail-value">{selectedStudent.dob}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{selectedStudent.address}</span>
                </div>
              </div>
              
              <div className="detail-group">
                <h4>Academic Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Institution:</span>
                  <span className="detail-value">{selectedStudent.institution}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Program:</span>
                  <span className="detail-value">{selectedStudent.program}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Department:</span>
                  <span className="detail-value">{selectedStudent.department}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current CGPA:</span>
                  <span className="detail-value">{selectedStudent.currentSemesterCgpa}</span>
                </div>
              </div>
              
              <div className="detail-group">
                <h4>Project Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Project:</span>
                  <span className="detail-value">
                    {selectedStudent.selected_project?.title || "None"}
                    {selectedStudent.selected_project && (
                      <span className="project-details">
                        (Professor: {selectedStudent.selected_project.professor?.name || 'Unknown'}, 
                        Department: {selectedStudent.selected_project.department?.name || 'Unknown'})
                      </span>
                    )}
                  </span>
                </div>
                
                {!selectedStudent.selected_project && availableProjects.length > 0 && (
                  <div className="project-controls">
                    <select
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Select a project</option>
                      {availableProjects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.title} (Pref: {getPreferenceNumber(project.id)})
                        </option>
                      ))}
                    </select>
                    
                    <button 
                      onClick={handleAllotProject}
                      disabled={loading || !selectedProject}
                      className="allot-btn"
                    >
                      {loading ? "Processing..." : "Allot Project"}
                    </button>
                  </div>
                )}
                
                {selectedStudent.selected_project && (
                  <div className="project-action-buttons">
                    <button 
                      onClick={handleRemoveProject}
                      disabled={loading}
                      className="remove-btn"
                    >
                      {loading ? "Processing..." : "Remove Project"}
                    </button>
                    
                    <div className="allot-pref-controls">
                      <select
                        value={allotPrefProject}
                        onChange={(e) => setAllotPrefProject(e.target.value)}
                        disabled={loading}
                      >
                        <option value="">Select project to allot</option>
                        {availableProjects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.title} (Pref: {getPreferenceNumber(project.id)})
                          </option>
                        ))}
                      </select>
                      
                      <button 
                        onClick={handleAllotPref}
                        disabled={loading || !allotPrefProject}
                        className="allot-pref-btn"
                      >
                        {loading ? "Processing..." : "Allot Preference"}
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="preferences">
                  <h5>Preferences</h5>
                  <ol>
                    <li>
                      {selectedStudent.pref1?.title || "Not specified"}
                      {selectedStudent.pref1 && (
                        <span className="preference-details">
                          (Professor: {selectedStudent.pref1.professor?.name || 'Unknown'}, 
                          Department: {selectedStudent.pref1.department?.name || 'Unknown'})
                        </span>
                      )}
                    </li>
                    <li>
                      {selectedStudent.pref2?.title || "Not specified"}
                      {selectedStudent.pref2 && (
                        <span className="preference-details">
                          (Professor: {selectedStudent.pref2.professor?.name || 'Unknown'}, 
                          Department: {selectedStudent.pref2.department?.name || 'Unknown'})
                        </span>
                      )}
                    </li>
                    <li>
                      {selectedStudent.pref3?.title || "Not specified"}
                      {selectedStudent.pref3 && (
                        <span className="preference-details">
                          (Professor: {selectedStudent.pref3.professor?.name || 'Unknown'}, 
                          Department: {selectedStudent.pref3.department?.name || 'Unknown'})
                        </span>
                      )}
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-student-selected">
              <i className="fas fa-user-graduate"></i>
              <p>Select a student from the list to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Helper function to get preference number for a project
  function getPreferenceNumber(projectId) {
    if (!selectedStudent) return '';
    if (selectedStudent.pref1?.id === projectId) return '1';
    if (selectedStudent.pref2?.id === projectId) return '2';
    if (selectedStudent.pref3?.id === projectId) return '3';
    return '';
  }
}

export default StudentsSection;